import { RULES } from "./rules.js";
import type { Game } from "./store.js";

/**
 * The six quick-entry scores (FR-3.6, reworked).
 *
 * The buttons used to be fixed at 350/400/450/500/1500/2000. They are now the
 * six scores this group banks most, so the shortcuts match how these people
 * actually play. It is computed from local history and needs no network; when
 * there is not enough history to be meaningful it falls back to a sensible
 * default set.
 */

/** Shown before a group has any real banking history to learn from. */
export const DEFAULT_QUICK = [350, 400, 450, 500, 750, 800] as const;

const SIZE = 6;

/**
 * A valid bank is >= 350 and a multiple of 50, so only those count -- a quick
 * button that the bank rule would reject is useless.
 */
function bankable(points: number): boolean {
  return points >= RULES.minBank && points % RULES.granularity === 0;
}

/**
 * The six most-banked scores across the given games, most frequent first, then
 * larger first so ties resolve predictably. Falls back to DEFAULT_QUICK until a
 * group has banked enough distinct scores to fill the row.
 */
export function quickScores(games: Game[]): number[] {
  const counts = new Map<number, number>();
  for (const game of games) {
    for (const turn of game.turns) {
      if (turn.type === "bank" && bankable(turn.points)) {
        counts.set(turn.points, (counts.get(turn.points) ?? 0) + 1);
      }
    }
  }

  const ranked = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || b[0] - a[0])
    .map(([points]) => points);

  // Which six to show is decided by frequency (most-banked first), then topped
  // up from the defaults when a group has not banked six distinct scores yet.
  const chosen = [...ranked];
  for (const fallback of DEFAULT_QUICK) {
    if (chosen.length >= SIZE) break;
    if (!chosen.includes(fallback)) chosen.push(fallback);
  }

  // ...but they are shown in ascending value order, so the row reads left to
  // right like a number line regardless of how often each score is used.
  return chosen.slice(0, SIZE).sort((a, b) => a - b);
}
